import * as React from 'react';
import { IButtonProps, INumberFieldProps, THeadingLevel } from 'telia-front-react';
import { observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import CustomerType from '../sso/CustomerType';
import config from '../Config';
import SsoStore from '../stores/SsoStore';
import { VAT_RATE } from '../util/PriceUtil';
import TemplateSafeCalculator from './TemplateSafeCalculator';

export interface SafeCalculatorProps {
  id: string;
  type: CustomerType;
  steps: PriceStep[];
  headingLevel?: THeadingLevel;
  ssoStore?: SsoStore;
}

interface PriceStep {
  minLicenses: number;
  price: number;
  increment?: number;
}

interface Result {
  price: number;
  regularPriceLabel: string;
  priceLabel: string;
  url: URL;
}

const MIN = 1;
const MAX = 50;

const URL_PATH_B2C = '/era/mobiil/lisateenused/telia-safe/tellimine';
const URL_PATH_B2B = '/ari/muud-teenused/telia-safe/tellimine';

const PRICE_LABEL_B2C = 'safe.B2C.priceLabel';
const REGULAR_PRICE_LABEL_B2C = 'safe.B2C.regularPriceLabel';
const PRICE_LABEL_B2B = 'safe.B2B.priceLabel';
const REGULAR_PRICE_LABEL_B2B = 'safe.B2B.regularPriceLabel';
const INPUT_LABEL = 'safe.inputLabel';
const BUTTON_LABEL = 'safe.buttonLabel';
const DEVICES_TITLE_LABEL = 'safe.devicesTitleLabel';
const RESULT_TITLE_LABEL = 'safe.resultTitleLabel';

const getPriceWithoutVat = (price: number): number => price / VAT_RATE;

const getBoundValidCount = (count: number) => (Number.isNaN(count) ? MIN : Math.min(Math.max(MIN, count), MAX));

const getPrice = (steps: PriceStep[], count: number): number => {
  const stepsLicenses = steps.map((step) => step.minLicenses).sort((n1, n2) => n1 - n2);
  const minDeltas = stepsLicenses.map((licenseCount) => licenseCount - count).filter((diff) => diff <= 0);
  const minLicenses = stepsLicenses[minDeltas.indexOf(Math.max(...minDeltas))];
  const newStep = steps.find((step) => step.minLicenses === minLicenses);
  return newStep ? newStep.price + (newStep.increment ? newStep.increment * (count - minLicenses) : 0) : 0;
};

const getResult = (price: number, type: CustomerType): Result => {
  switch (type) {
    case CustomerType.B2B:
      return {
        price: getPriceWithoutVat(price),
        priceLabel: PRICE_LABEL_B2B,
        regularPriceLabel: REGULAR_PRICE_LABEL_B2B,
        url: new URL(URL_PATH_B2B, config.teliaWebSelfServiceUrl),
      };
    default:
      return {
        price,
        priceLabel: PRICE_LABEL_B2C,
        regularPriceLabel: REGULAR_PRICE_LABEL_B2C,
        url: new URL(URL_PATH_B2C, config.teliaWebSelfServiceUrl),
      };
  }
};

@observer
class SafeCalculator extends React.Component<SafeCalculatorProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  @observable accessor count: number = MIN;

  public onChange = (newValue: number) =>
    runInAction(() => {
      this.count = getBoundValidCount(newValue);
    });

  public render() {
    const translate = this.context.translate;
    const { id, steps, type, headingLevel, ssoStore } = this.props;

    const price = getPrice(steps, this.count);
    const result = getResult(price, type);
    result.url.searchParams.set('amount', this.count.toString());
    const formattedPrice = result.price.toFixed(2);

    const numberInputProps: INumberFieldProps = {
      id,
      label: translate(INPUT_LABEL),
      decreaseButtonText: null,
      increaseButtonText: null,
      min: MIN,
      max: MAX,
      value: this.count,
      onChange: this.onChange,
    };

    const resultButtonProps: IButtonProps = {
      text: translate(BUTTON_LABEL),
      onClick: () => ssoStore.ensureLogin(result.url.toString()),
    };

    return (
      <TemplateSafeCalculator
        numberInput={numberInputProps}
        devicesTitle={translate(DEVICES_TITLE_LABEL)}
        resultTitle={translate(RESULT_TITLE_LABEL)}
        resultPrice={translate(result.priceLabel, { price: formattedPrice })}
        resultRegularPrice={translate(result.regularPriceLabel, {
          price: formattedPrice,
        })}
        resultButton={resultButtonProps}
        headingLevel={headingLevel}
      />
    );
  }
}

export default inject('ssoStore')(SafeCalculator);
